import React, { useEffect, useState, useRef, useCallback } from "react";
import "../Dashboard/HRManagerDashboard.css";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Tag, DatePicker, message, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, MinusOutlined } from "@ant-design/icons";
import { svg } from "../../../Assets/svg";
import { hr } from "../../../Assets/hr";
import { getDecryptedUserData, decryptByText, setEncryptedUserData } from "../../../helpers/bycrypt";
import {
  ROLE,
  rangePickerProps,
  rangePresets,
  getFullAwsUrl,
  IS_WEB_ATTENDANCE_ENABLED,
  useDebounce,
} from "../../../helpers/utils";
import {
  formatTimeWithBusinessTZ,
  formatDateWithBusinessTZ,
  convertLocalDayjsToBusinessUTC,
  getBusinessTimeZone,
  getEmploymentEndDateString,
} from "../../../helpers/moment";
import { formatSecondsToHHMM } from "../../../helpers/common";
import moment from "moment-timezone";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { GetEmployeeAttendanceDashboardAPI } from "../../Redux/dashboard";
import { GetTeamMemberListAPI } from "../../Redux/teamMember";
import { GetProfileAPI } from "../../../Auth/Redux/auth";
import axiosInstance from "../../../helpers/axios";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import AutoCloseTooltip from "../../Components/AutoCloseTooltip";
import { Dropdown, Menu } from "antd";
import CommonDeleteModal from "../../Components/Modals/CommonDeleteModal";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import ModifyAttendanceTimeModal from "../Dashboard/Components/Modals/ModifyAttendanceTimeModal";
import { DeleteEmployeeAttendanceAPI } from "../../Redux/dashboard";
import {
  PagePermissionNameData,
  usePermissionsChecker,
  RedirectLoginPermissionArray,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import { BookTimeOffModal } from "../TeamMembers/Components/Modals/BookTimeOffModal";
import FaceClockInOut from "../../../Components/FaceClockInOut";
import { Capacitor } from "@capacitor/core";
import { useSocket } from "../../../contexts/SocketContext";
import PageNotFound from "../../../Components/Error";
import DayNotesDetailModal from "./DayNotesDetailModal";
import { useIsTimeVariant } from "../../../helpers/appPackage";

const { RangePicker } = DatePicker;

dayjs.extend(isoWeek);

const SummaryTicker: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className, style }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isClicked, setIsClicked] = React.useState(false);

  React.useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && measureRef.current) {
        const next = measureRef.current.scrollWidth > containerRef.current.clientWidth;
        setIsOverflowing((prev) => (prev !== next ? next : prev));
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    const timer = setTimeout(checkOverflow, 200);
    return () => {
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timer);
    };
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsClicked(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isPaused = isHovered || isClicked;

  return (
    <div
      ref={containerRef}
      className={`${className || ''} ${isOverflowing ? 'overflow-hidden' : ''}`}
      style={{ whiteSpace: "nowrap", minWidth: 0, display: "flex", alignItems: "center", ...style }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsClicked(true)}
    >
      <div ref={measureRef} style={{ visibility: "hidden", position: "absolute", whiteSpace: "nowrap", pointerEvents: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        {children}
      </div>
      {isOverflowing ? (
        <div
          className="summary-ticker-anim"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            paddingLeft: "100%",
            animationPlayState: isPaused ? 'paused' : 'running'
          }}
        >
          {children}
        </div>
      ) : (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>{children}</div>
      )}
    </div>
  );
};

const ClockInHistory = ({
  activeDashboard,
  setActiveDashboard,
  isPersonalHistory,
}: any) => {
  const isTimeVariant = useIsTimeVariant();
  const tz = getBusinessTimeZone();
  const businessToday = dayjs(moment().tz(tz).format("YYYY-MM-DD"));

  const dynamicRangePresets: any = [
    { label: "Lifetime", value: [dayjs("1970-01-01"), businessToday] },
    {
      label: "Today",
      value: [businessToday.startOf("day"), businessToday.endOf("day")],
    },
    {
      label: "This Week",
      value: [businessToday.startOf("isoWeek"), businessToday.endOf("isoWeek")],
    },
    {
      label: "Last Week",
      value: [
        businessToday.subtract(1, "week").startOf("isoWeek"),
        businessToday.subtract(1, "week").endOf("isoWeek"),
      ],
    },
    {
      label: "This Bi-Weekly",
      value: [
        businessToday.startOf("isoWeek"),
        businessToday.add(1, "week").endOf("isoWeek"),
      ],
    },
    {
      label: "Prev Bi-Weekly",
      value: [
        businessToday.subtract(2, "week").startOf("isoWeek"),
        businessToday.subtract(1, "week").endOf("isoWeek"),
      ],
    },
    {
      label: "Last 7 Days",
      value: [businessToday.subtract(7, "d"), businessToday],
    },
    {
      label: "Last 14 Days",
      value: [businessToday.subtract(14, "d"), businessToday],
    },
    {
      label: "Last 30 Days",
      value: [businessToday.subtract(30, "d"), businessToday],
    },
    {
      label: "This Month",
      value: [businessToday.startOf("month"), businessToday.endOf("month")],
    },
    {
      label: "Last Month",
      value: [
        businessToday.subtract(1, "month").startOf("month"),
        businessToday.subtract(1, "month").endOf("month"),
      ],
    },
  ];

  const userData = getDecryptedUserData();
  const userRole = userData?.role?.key || userData?.role;
  const userRolesList = Array.isArray(userData?.roles)
    ? userData.roles.map((r: any) => (typeof r === "object" ? r?.key : r))
    : [];

  const adminRoles = [
    ROLE.ADMIN,
    ROLE.SUB_ADMIN,
    (ROLE as any).HR,
    (ROLE as any).HR_MANAGER,
  ];

  const isAdminOrManager =
    adminRoles.includes(userRole) ||
    userRolesList.some((r: string) => adminRoles.includes(r)) ||
    userData?.isEmployee === false;

  const dispatch = useAppDispatch();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.CLOCK_IN_HISTORY,
  );

  const { getEmployeeAttendanceDashboardLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );
  const { employeeAttendanceData } = useAppSelector(
    (state: RootState) => state.dashboard,
  );

  const { teamMemberList } = useAppSelector(
    (state: RootState) => state.teamMember,
  );

  const { authLoading } = useAppSelector((state: RootState) => state?.loading);

  const { id } = useParams();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<any>([
    businessToday.startOf("day"),
    businessToday.endOf("day"),
  ]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>(
    isPersonalHistory ? userData?._id : id || "",
  );
  const [isModifyModalVisible, setIsModifyModalVisible] = useState(false);
  const [modifyData, setModifyData] = useState<any>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteData, setDeleteData] = useState<any>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<string>("");
  const [openBookTimeOffModal, setOpenBookTimeOffModal] = useState(false);
  const [isDayNoteModalOpen, setIsDayNoteModalOpen] = useState(false);
  const [selectedDayNoteDate, setSelectedDayNoteDate] = useState<string | undefined>(undefined);
  const [selectedDayNotePayrollApproved, setSelectedDayNotePayrollApproved] = useState<boolean | undefined>(undefined);
  const [isPersonalFaceScanModalOpen, setIsPersonalFaceScanModalOpen] =
    useState(false);
  const [employeePage, setEmployeePage] = useState(1);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(true);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const debouncedEmployeeSearch = useDebounce(employeeSearch, 300);
  const { deleteEmployeeAttendanceLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );
  const { socket } = useSocket();

  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const selectedEmployeeRef = useRef(selectedEmployee);
  selectedEmployeeRef.current = selectedEmployee;

  const userDataRef = useRef(userData);
  userDataRef.current = userData;

  const idRef = useRef(id);
  idRef.current = id;

  const teamMemberListRef = useRef(teamMemberList);
  teamMemberListRef.current = teamMemberList;

  const hasLoadedOnce = useRef(false);
  const isFetchingAttendanceRef = useRef(false);
  const lastFetchedKeyRef = useRef("");

  const fetchAttendance = useCallback(
    async (isSilent = false, force = false) => {
      if (isPersonalHistory && userDataRef.current && userDataRef.current.isEmployee === false) return;
      let start = "";
      let end = "";
      const currentRange = dateRangeRef.current;
      if (currentRange && currentRange.length === 2) {
        start = convertLocalDayjsToBusinessUTC(currentRange[0].startOf("day")) || "";
        end = convertLocalDayjsToBusinessUTC(currentRange[1].endOf("day")) || "";
      }
      const currentEmp = isPersonalHistory
        ? userDataRef.current?._id
        : selectedEmployeeRef.current || idRef.current || "";

      if (!currentEmp) return;

      const requestKey = `${start}_${end}_${currentEmp}`;

      // Prevent calling if this exact request is already in-flight (unless force is requested)
      if (isFetchingAttendanceRef.current && lastFetchedKeyRef.current === requestKey && !force) {
        return;
      }

      isFetchingAttendanceRef.current = true;
      lastFetchedKeyRef.current = requestKey;

      try {
        await dispatch(
          GetEmployeeAttendanceDashboardAPI({
            start,
            end,
            user: currentEmp,
            isSilent,
            force,
          }) as any,
        );
      } finally {
        isFetchingAttendanceRef.current = false;
        hasLoadedOnce.current = true;
      }
    },
    [isPersonalHistory, dispatch],
  );

  useEffect(() => {
    if (socket) {
      const handleProfileUpdate = (data: any) => {
        const currentEmpId = isPersonalHistory
          ? userDataRef.current?._id
          : selectedEmployeeRef.current || idRef.current || userDataRef.current?._id;

        const isCurrentView = !data?.userId || data?.userId === currentEmpId;
        const isSelf = data?.userId === userDataRef.current?._id;

        if (isSelf) {
          dispatch(GetProfileAPI(navigate, undefined, undefined, true) as any);
        }

        if (isCurrentView) {
          fetchAttendance(true);
        }
      };

      const handleProfileCompleted = (data: any) => {
        // Set new employee in state directly so dropdown updates immediately
        if (data?.employee) {
          const currentList = teamMemberListRef.current || [];
          const exists = currentList.some(
            (e: any) => (e._id || e.user) === (data.employee._id || data.employee.user),
          );
          if (!exists) {
            dispatch({
              type: "GET_TEAM_MEMBER_LIST",
              payload: {
                data: [data.employee, ...currentList],
                count: currentList.length + 1,
              },
            });
          }
        }

        // Silently update employee list in background so all populated fields stay in sync
        dispatch(
          GetTeamMemberListAPI({
            limit: 30,
            page: 1,
            isEmployee: true,
            is_active: 1,
            employee_profile_status: "Completed",
            isSilent: true,
          }),
        );

        const currentEmpId = isPersonalHistory
          ? userDataRef.current?._id
          : selectedEmployeeRef.current || idRef.current || userDataRef.current?._id;

        // If the newly completed employee is the one currently being viewed, refresh silently
        if (data?.employee?._id && data.employee._id === currentEmpId) {
          fetchAttendance(true);
        }
      };

      socket.on("employee-sync-required", handleProfileUpdate);
      socket.on("hr-dashboard-profile-completed", handleProfileCompleted);

      return () => {
        socket.off("employee-sync-required", handleProfileUpdate);
        socket.off("hr-dashboard-profile-completed", handleProfileCompleted);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isPersonalHistory, dispatch, navigate]);

  useEffect(() => {
    if (socket) {
      const handleHistoryUpdate = (resData: any[]) => {
        if (!resData || resData.length === 0) return;
        const userObj = resData[0];

        if (userObj._id === userData?._id || userObj._id === id || userObj._id === selectedEmployee) {
          const finalPayload = (userObj.attendance || []).map((att: any) => {
            let formattedTotal = att.total || att.total_time;
            if (!formattedTotal && att.created_at) {
              const start = new Date(att.created_at).getTime();
              const end = att.clockOut && att.updated_at ? new Date(att.updated_at).getTime() : Date.now();
              const diffSeconds = Math.floor((end - start) / 1000);
              if (diffSeconds > 0) {
                formattedTotal = formatSecondsToHHMM(diffSeconds);
              }
            }
            return {
              ...att,
              user: {
                first_name: userObj.first_name,
                last_name: userObj.last_name,
                profile_pic: userObj.profile_pic
              },
              day_note_dates: userObj.day_note_dates || [],
              created_at: att.created_at,
              updated_at: att.updated_at,
              total: formattedTotal || "00:00:00"
            };
          }).sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

          finalPayload.day_note_dates = userObj.day_note_dates || [];

          dispatch({ type: "GET_HR_ATTENDANCE_DATA", payload: finalPayload });

          if (userObj._id === userData?._id) {
            const isClockedIn = finalPayload.length > 0 && !finalPayload[0].clockOut;
            const isClockedOut = finalPayload.length > 0 && !!finalPayload[0].clockOut;

            const freshUserData = getDecryptedUserData();
            const updatedUserData = {
              ...freshUserData,
              isClockedIn,
              isClockedOut
            };
            setEncryptedUserData(updatedUserData);

            dispatch({
              type: "LOGIN_SUCCESS",
              payload: updatedUserData
            });
          }
        }
      };

      socket.on("clock-in-history-update", handleHistoryUpdate);
      return () => {
        socket.off("clock-in-history-update", handleHistoryUpdate);
      };
    }
  }, [socket, userData?._id, id, selectedEmployee]);

  useEffect(() => {
    if (isPersonalHistory && userData && userData.isEmployee === false) {
      const findRedirectPath = RedirectLoginPermissionArray?.find(
        (ele: any) =>
          ele.path !== "/clock-in-history" &&
          userData?.role?.permission?.some(
            (per: any) => per?.name === ele?.name,
          ),
      );

      if (findRedirectPath?.path) {
        navigate(findRedirectPath.path, { replace: true });
      } else {
        navigate("/404", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPersonalHistory, navigate]);

  const isNative = Capacitor.isNativePlatform();
  // Web face scan is allowed when REACT_APP_ENABLE_WEB_ATTENDANCE=true
  // (dev servers only — the variable defaults to false when missing).
  const isWebFaceScanEnabled = IS_WEB_ATTENDANCE_ENABLED;
  const canOpenFaceScanModal = isNative || isWebFaceScanEnabled;
  const handleFaceScanUnavailable = () => {
    if (!canOpenFaceScanModal) {
      message.error("Clock in/out is not supported on web.");
      return;
    }
    navigate("/face-scanner");
  };

  let empStartDate: any = null;
  const currentEmpId = isPersonalHistory
    ? userData?._id
    : id || selectedEmployee;

  if (currentEmpId && teamMemberList && teamMemberList.length > 0) {
    const emp = teamMemberList.find((e: any) => e._id === currentEmpId);
    const start_date =
      emp?.employee_details?.basic_details?.employment_start_date ||
      emp?.basic_details?.employment_start_date;
    if (start_date) {
      empStartDate = dayjs(start_date).startOf("day");
    }
  }

  if (!empStartDate && isPersonalHistory) {
    const userStartDate =
      userData?.employee_details?.basic_details?.employment_start_date ||
      userData?.basic_details?.employment_start_date;
    if (userStartDate) {
      empStartDate = dayjs(userStartDate).startOf("day");
    }
  }

  const isBeforeStart = empStartDate
    ? dayjs().isBefore(empStartDate, "day")
    : false;

  const selectedEmpDetail = React.useMemo(() => {
    const empIdToUse = isPersonalHistory ? userData?._id : (selectedEmployee || id);
    if (!empIdToUse) return null;
    return teamMemberList?.find(
      (emp: any) => emp?.user === empIdToUse || emp?.user?._id === empIdToUse || emp?._id === empIdToUse
    );
  }, [selectedEmployee, teamMemberList, isPersonalHistory, userData, id]);

  const empEndDateStr = React.useMemo(() => {
    const empIdToUse = isPersonalHistory ? userData?._id : (selectedEmployee || id);
    if (empIdToUse && teamMemberList && teamMemberList.length > 0) {
      const emp = teamMemberList.find(
        (e: any) =>
          e._id === empIdToUse ||
          e?.user === empIdToUse ||
          e?.user?._id === empIdToUse,
      );
      const raw =
        emp?.employment_end_date ||
        emp?.employee_details?.basic_details?.employment_end_date ||
        emp?.basic_details?.employment_end_date;
      if (raw) return getEmploymentEndDateString(raw);
    }

    if (selectedEmpDetail) {
      const raw =
        selectedEmpDetail?.employment_end_date ||
        selectedEmpDetail?.employee_details?.basic_details?.employment_end_date ||
        selectedEmpDetail?.basic_details?.employment_end_date;
      if (raw) return getEmploymentEndDateString(raw);
    }

    if (isPersonalHistory) {
      const raw =
        userData?.employment_end_date ||
        userData?.employee_details?.basic_details?.employment_end_date ||
        userData?.basic_details?.employment_end_date;
      if (raw) return getEmploymentEndDateString(raw);
    }

    return "";
  }, [selectedEmployee, id, teamMemberList, selectedEmpDetail, isPersonalHistory, userData]);

  const todayDateStr = formatDateWithBusinessTZ(new Date(), "YYYY-MM-DD");
  const isEmploymentEnded = Boolean(empEndDateStr && todayDateStr > empEndDateStr);

  const isPayWithinSchedule = React.useMemo(() => {
    const empIdToUse = isPersonalHistory ? userData?._id : (selectedEmployee || id);
    if (!empIdToUse) return true;
    const details = selectedEmpDetail?.employee_details || selectedEmpDetail;
    if (!details || !details.schedule || !Array.isArray(details.schedule)) return false;
    const duration = details.payroll_details?.duration?.toLowerCase();
    if (duration !== "weekly" && duration !== "biweekly") return false;

    const targetDate = dateRange && dateRange[1] ? dateRange[1].toDate() : new Date();

    const sorted = [...details.schedule].sort(
      (a: any, b: any) => new Date(b.effective_date || b.created_at).getTime() - new Date(a.effective_date || a.created_at).getTime()
    );
    let active = sorted.find((s: any) => new Date(s.effective_date || s.created_at) <= targetDate);
    if (!active) {
      active = sorted[0];
    } else if (sorted[0]?.payWithinSchedule) {
      active = sorted[0];
    }

    if (!active) return false;
    return !!active.payWithinSchedule;
  }, [selectedEmpDetail, dateRange, selectedEmployee, isPersonalHistory, userData, id]);

  const showTimeOffHours = React.useMemo(() => {
    const details = selectedEmpDetail?.employee_details || selectedEmpDetail;
    const duration = details?.payroll_details?.duration?.toLowerCase();
    const isMonthly = duration ? duration === "monthly" : true;
    const hasDeduction = (employeeAttendanceData || []).some(
      (item: any) => item.time_off_deducted_time && Number(item.time_off_deducted_time) > 0,
    );
    return isMonthly && hasDeduction;
  }, [selectedEmpDetail, employeeAttendanceData]);

  useEffect(() => {
    if (isPersonalHistory && userData && userData.isEmployee === false) return;
    setEmployeePage(1);
    dispatch(
      GetTeamMemberListAPI(
        {
          limit: 30,
          page: 1,
          isEmployee: true,
          is_active: 1,
          isActive: true,
          employee_profile_status: "Completed",
          search: debouncedEmployeeSearch,
        },
        (hasMore: boolean) => setHasMoreEmployees(hasMore),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, debouncedEmployeeSearch]);

  const handleEmployeePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { target } = e as any;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      hasMoreEmployees
    ) {
      const nextPage = employeePage + 1;
      setEmployeePage(nextPage);
      dispatch(
        GetTeamMemberListAPI(
          {
            limit: 30,
            page: nextPage,
            isEmployee: true,
            is_active: 1,
            isActive: true,
            employee_profile_status: "Completed",
            search: debouncedEmployeeSearch,
          },
          (hasMore: boolean) => setHasMoreEmployees(hasMore),
        ),
      );
    }
  };

  const dateRangeKey = React.useMemo(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      return `${dateRange[0].format?.("YYYY-MM-DD") || ""}_${dateRange[1].format?.("YYYY-MM-DD") || ""}`;
    }
    return "";
  }, [dateRange]);

  useEffect(() => {
    if (isPersonalHistory && userDataRef.current && userDataRef.current.isEmployee === false) return;
    fetchAttendance(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeKey, selectedEmployee]);

  const calculateTotalBreakTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let breakSecs = 0;
    data.forEach((item) => {
      if (item.break_deduction?.is_deducted && item.break_deduction?.deducted_minutes) {
        breakSecs += item.break_deduction.deducted_minutes * 60;
      }
    });
    return formatSecondsToHHMM(breakSecs);
  };

  const calculateTotalTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let totalSeconds = 0;
    data.forEach((item) => {
      if (item.clockOut && item.total) {
        const parts = item.total.split(":");
        if (parts.length === 3) {
          totalSeconds +=
            parseInt(parts[0], 10) * 3600 +
            parseInt(parts[1], 10) * 60 +
            parseInt(parts[2], 10);
        } else if (parts.length === 2) {
          totalSeconds +=
            parseInt(parts[0], 10) * 3600 +
            parseInt(parts[1], 10) * 60;
        }
      }
    });
    return formatSecondsToHHMM(totalSeconds);
  };

  const calculateTotalLateTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let totalLateMinutes = 0;
    data.forEach((item) => {
      if (item.late_deduceted_time && !isNaN(item.late_deduceted_time)) {
        totalLateMinutes += Number(item.late_deduceted_time);
      }
    });
    const hrs = Math.floor(totalLateMinutes / 60);
    const mins = totalLateMinutes % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const calculateTotalRoundOffTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let roundOffMs = 0;
    data.forEach((item) => {
      if (item.round_off_time !== undefined && item.round_off_time !== null && !isNaN(item.round_off_time)) {
        roundOffMs += Number(item.round_off_time);
      }
    });
    return formatSecondsToHHMM(Math.floor(roundOffMs / 1000));
  };

  const calculateTotalUnscheduledTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let totalUnscheduledMins = 0;
    data.forEach((item) => {
      if (item.unschedule_hours && !isNaN(item.unschedule_hours)) {
        totalUnscheduledMins += Number(item.unschedule_hours);
      }
    });
    const hrs = Math.floor(totalUnscheduledMins / 60);
    const mins = totalUnscheduledMins % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const calculateTotalTimeOffTime = (data: any[]) => {
    if (!data || !data.length) return "00:00";
    let timeOffMs = 0;
    data.forEach((item) => {
      if (item.time_off_deducted_time && !isNaN(item.time_off_deducted_time)) {
        timeOffMs += Number(item.time_off_deducted_time);
      }
    });
    return formatSecondsToHHMM(Math.floor(timeOffMs / 1000));
  };

  const totalBreakStr = calculateTotalBreakTime(employeeAttendanceData || []);
  const totalTimeStr = calculateTotalTime(employeeAttendanceData || []);
  const totalTimeOffStr = calculateTotalTimeOffTime(employeeAttendanceData || []);
  const totalLateStr = calculateTotalLateTime(employeeAttendanceData || []);
  const totalUnscheduledStr = calculateTotalUnscheduledTime(employeeAttendanceData || []);
  const totalRoundOffStr = calculateTotalRoundOffTime(employeeAttendanceData || []);

  const noteDatesSet = React.useMemo(() => {
    const set = new Set<string>();
    if (employeeAttendanceData) {
      if (Array.isArray((employeeAttendanceData as any).day_note_dates)) {
        (employeeAttendanceData as any).day_note_dates.forEach((d: string) => set.add(d));
      }
      if (Array.isArray(employeeAttendanceData)) {
        employeeAttendanceData.forEach((rec: any) => {
          if (Array.isArray(rec.day_note_dates)) {
            rec.day_note_dates.forEach((d: string) => set.add(d));
          }
        });
      }
    }
    return set;
  }, [employeeAttendanceData]);

  const groupedData = React.useMemo(() => {
    if (!employeeAttendanceData) return [];

    const isSingleDay =
      dateRange &&
      dateRange.length === 2 &&
      dateRange[0].isSame(dateRange[1], "day");

    if (isSingleDay) {
      const singleDateStr = dateRange[0].format("YYYY-MM-DD");
      const hasNoteForSingleDay = noteDatesSet.has(singleDateStr);

      if (employeeAttendanceData.length === 0) {
        return [
          {
            key: singleDateStr,
            isGroup: false,
            date: singleDateStr,
            isMissing: true,
            has_day_note: hasNoteForSingleDay,
            isPayrollApproved: Boolean(
              (employeeAttendanceData as any)?.isPayrollApproved ||
              (employeeAttendanceData as any)?.is_payroll_approved
            ),
            created_at:
              convertLocalDayjsToBusinessUTC(dateRange[0].startOf("day")) || "",
          },
        ];
      }
      return [...employeeAttendanceData]
        .sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .map((record: any) => {
          const recordDateStr = formatDateWithBusinessTZ(record.created_at, "YYYY-MM-DD");
          return {
            ...record,
            isGroup: false,
            date: recordDateStr,
            has_day_note: noteDatesSet.has(recordDateStr),
            isPayrollApproved: Boolean(record.isPayrollApproved || record.is_payroll_approved),
            key: record._id || Math.random().toString(),
          };
        });
    }

    const groups: { [key: string]: any[] } = {};
    employeeAttendanceData.forEach((record: any) => {
      const dateStr = formatDateWithBusinessTZ(record.created_at, "YYYY-MM-DD");
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(record);
    });

    const result: any[] = [];
    if (dateRange && dateRange.length === 2) {
      let currentDay = dayjs(dateRange[1]).startOf("day");
      const startDate = dayjs(dateRange[0]).startOf("day");

      while (currentDay.unix() >= startDate.unix()) {
        const dateStr = currentDay.format("YYYY-MM-DD");
        const records = groups[dateStr] || [];
        const hasDayNote = noteDatesSet.has(dateStr);

        if (records.length === 0) {
          result.push({
            key: dateStr,
            isGroup: false,
            date: dateStr,
            isMissing: true,
            has_day_note: hasDayNote,
            isPayrollApproved: false,
            created_at:
              convertLocalDayjsToBusinessUTC(currentDay.startOf("day")) || "", // for Date column display
          });
        } else {
          const firstRecord = records[records.length - 1]; // earliest since it's sorted desc
          const lastRecord = records[0]; // latest

          const isPayrollApproved = records.some(
            (r: any) => r.isPayrollApproved === true || r.is_payroll_approved === true
          );

          const sortedPunches = [...records].sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          let totalSecs = 0;
          let breakSecs = 0;
          let lateMins = 0;
          let unscheduleMins = 0;
          let roundOffMs = 0;
          let timeOffDeductedMs = 0;

          records.forEach((item) => {
            if (item.clockOut && item.total) {
              const parts = item.total.split(":");
              if (parts.length === 3) {
                totalSecs += parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
              } else if (parts.length === 2) {
                totalSecs += parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60;
              }
            }
            if (item.late_deduceted_time && !isNaN(item.late_deduceted_time)) {
              lateMins += Number(item.late_deduceted_time);
            }
            if (item.unschedule_hours && !isNaN(item.unschedule_hours)) {
              unscheduleMins += Number(item.unschedule_hours);
            }
            if (item.time_off_deducted_time && !isNaN(item.time_off_deducted_time)) {
              timeOffDeductedMs += Number(item.time_off_deducted_time);
            }
            if (item.round_off_time !== undefined && item.round_off_time !== null && !isNaN(item.round_off_time)) {
              roundOffMs += Number(item.round_off_time);
            }
          });

          const deductedLog = records.find((p: any) => p.break_deduction?.is_deducted);
          if (deductedLog?.break_deduction?.deducted_minutes) {
            breakSecs = deductedLog.break_deduction.deducted_minutes * 60;
          }

          const timeOffSecs = Math.floor(timeOffDeductedMs / 1000);
          const roundOffSecs = Math.floor(roundOffMs / 1000);
          const workSecs = Math.max(0, totalSecs - breakSecs - (lateMins * 60) - (unscheduleMins * 60) - timeOffSecs);
          const formatSecs = (s: number) => formatSecondsToHHMM(s);

          result.push({
            key: dateStr,
            isGroup: true,
            date: dateStr,
            has_day_note: hasDayNote,
            isPayrollApproved: isPayrollApproved,
            created_at: firstRecord?.created_at,
            clockIn: firstRecord?.clockIn,
            clockOut: lastRecord?.clockOut,
            updated_at:
              lastRecord?.updated_at || lastRecord?.clockOut?.created_at,
            punches: sortedPunches,
            total: calculateTotalTime(records),
            total_time: formatSecs(totalSecs),
            time_off_hours: formatSecs(timeOffSecs),
            time_off_deducted_time: timeOffDeductedMs,
            break_time: formatSecs(breakSecs),
            working_time: formatSecs(workSecs),
            late_time: formatSecs(lateMins * 60),
            unschedule_time: formatSecs(unscheduleMins * 60),
            round_off_time: formatSecs(roundOffSecs),
            user: firstRecord?.user,
          });
        }
        currentDay = currentDay.subtract(1, "day");
      }
    }

    return result;
  }, [employeeAttendanceData, dateRange]);

  const columns: ColumnsType<any> = [
    // {
    //   title: "Off Site",
    //   key: "onSite",
    //   align: 'center',
    //   render: (_, record) => record.isMissing ? "-" : (record.clockIn?.onSite ? <CheckCircleOutlined style={{ color: 'green', fontSize: '18px' }} /> : <CloseCircleOutlined style={{ color: 'red', fontSize: '18px' }} />),
    // },
    {
      title: "Date",
      key: "date",
      align: "center",
      render: (_, record) => {
        if (!record.created_at) return "-";
        return formatDateWithBusinessTZ(record.created_at);
      },
    },
    {
      title: "Clock In",
      dataIndex: "clockIn",
      key: "checkIn",
      render: (clockIn, record) => {
        if (record.isMissing) return "---";
        if (!record.created_at) return "---";
        const icon =
          clockIn?.biometric_authentication_by === "face"
            ? hr.face
            : clockIn?.biometric_authentication_by === "fingerprint"
              ? hr.fingerprint
              : null;
        return (
          <div className="d-flex align-items-center justify-content-between w-100 gap-3">
            <span>{formatTimeWithBusinessTZ(record.created_at)}</span>
            {/* <div className="d-flex align-items-center gap-2">
              {!isTimeVariant && icon && (
                <AutoCloseTooltip
                  title={
                    clockIn?.biometric_authentication_by === "face"
                      ? "Face Recognition"
                      : "Fingerprint"
                  }
                >
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: clockIn?.biometric_authentication?.face?.photo
                        ? "pointer"
                        : "default",
                    }}
                    onClick={() => {
                      if (clockIn?.biometric_authentication?.face?.photo) {
                        setPreviewPhoto(
                          clockIn.biometric_authentication.face.photo,
                        );
                        setIsPreviewModalOpen(true);
                      }
                    }}
                  >
                    <div
                      style={{
                        transform: "scale(0.35)",
                        transformOrigin: "center",
                      }}
                    >
                      {icon}
                    </div>
                  </span>
                </AutoCloseTooltip>
              )}
              <AutoCloseTooltip
                title={
                  clockIn?.ppe_compliant
                    ? "Weared PPE Mask Gloves"
                    : "Not Weared PPE Mask Gloves"
                }
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      transform: "scale(0.35)",
                      transformOrigin: "center",
                    }}
                  >
                    {clockIn?.ppe_compliant
                      ? hr.ppe_compliant.wear
                      : hr.ppe_compliant.not_wear}
                  </div>
                </span>
              </AutoCloseTooltip>
            </div> */}
          </div>
        );
      },
    },
    {
      title: "Clock Out",
      dataIndex: "clockOut",
      key: "checkOut",
      render: (clockOut, record) => {
        if (record.isMissing) return "---";
        if (!clockOut || !record.updated_at) return "-";
        const icon =
          clockOut.biometric_authentication_by === "face"
            ? hr.face
            : clockOut.biometric_authentication_by === "fingerprint"
              ? hr.fingerprint
              : null;
        return (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            <span>{formatTimeWithBusinessTZ(record.updated_at)}</span>
            {/* <div className="d-flex align-items-center gap-3">
              {!isTimeVariant && icon && (
                <AutoCloseTooltip
                  title={
                    clockOut.biometric_authentication_by === "face"
                      ? "Face Recognition"
                      : "Fingerprint"
                  }
                >
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: clockOut?.biometric_authentication?.face?.photo
                        ? "pointer"
                        : "default",
                    }}
                    onClick={() => {
                      if (clockOut?.biometric_authentication?.face?.photo) {
                        setPreviewPhoto(
                          clockOut.biometric_authentication.face.photo,
                        );
                        setIsPreviewModalOpen(true);
                      }
                    }}
                  >
                    <div
                      style={{
                        transform: "scale(0.35)",
                        transformOrigin: "center",
                      }}
                    >
                      {icon}
                    </div>
                  </span>
                </AutoCloseTooltip>
              )}
            </div> */}
          </div>
        );
      },
    },
    {
      title: `Clocked Hours (${totalTimeStr})`,
      dataIndex: "total",
      key: "total",
      align: "center",
      render: (total, record) => (record.isMissing ? "---" : (record.clockOut && total) ? total : "-"),
    },
    ...((dateRange && dateRange.length === 2 && dateRange[0].isSame(dateRange[1], 'day')) ? [] : [
      ...(showTimeOffHours
        ? [
          {
            title: "Time Off Hours",
            key: "time_off_hours",
            align: 'center' as const,
            render: (_: any, record: any) => {
              if (!record.isGroup || record.isMissing) return "-";
              const val = record.time_off_hours || "00:00";
              return <span className="d-flex align-items-center justify-content-center gap-1">- {val}</span>;
            },
          },
        ]
        : []),
      {
        title: "Late Time",
        key: "late_time",
        align: 'center' as const,
        render: (_: any, record: any) => {
          if (!record.isGroup || record.isMissing) return "-";
          const val = record.late_time || "00:00:00";
          return <span className="d-flex align-items-center justify-content-center gap-1">- {val}</span>;
        },
      },
      {
        title: "Break Time",
        key: "break_time",
        align: 'center' as const,
        render: (_: any, record: any) => {
          if (!record.isGroup || record.isMissing) return "-";
          const val = record.break_time || "00:00:00";
          return <span className="d-flex align-items-center justify-content-center gap-1">- {val}</span>;
        },
      },
      ...(isPayWithinSchedule
        ? [
          {
            title: "Unscheduled Hours",
            key: "unschedule_time",
            align: 'center' as const,
            render: (_: any, record: any) => {
              if (!record.isGroup || record.isMissing) return "-";
              const val = record.unschedule_time || "00:00:00";
              return <span className="d-flex align-items-center justify-content-center gap-1">- {val}</span>;
            },
          },
        ]
        : []),

      {
        title: "Working Time",
        key: "working_time",
        align: 'center' as const,
        render: (_: any, record: any) => {
          if (!record.isGroup || record.isMissing) return "-";
          return record.working_time || record.total || "00:00:00";
        },
      },
      {
        title: "Round Off Time",
        key: "round_off_time",
        align: 'center' as const,
        render: (_: any, record: any) => {
          if (!record.isGroup || record.isMissing) return "-";
          return record.round_off_time || "00:00";
        },
      }
    ]),
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => {
        const rowDate =
          record.date ||
          (record.created_at
            ? formatDateWithBusinessTZ(record.created_at, "YYYY-MM-DD")
            : undefined);

        const groupMenu = (
          <Menu>
            <Menu.Item
              key="view"
              onClick={() => {
                const selectedDay = dayjs(record.date);
                setDateRange([
                  selectedDay.startOf("day"),
                  selectedDay.endOf("day"),
                ]);
              }}
            >
              View Details
            </Menu.Item>
          </Menu>
        );

        const isSingleDayRange =
          dateRange &&
          dateRange.length === 2 &&
          dateRange[0].isSame(dateRange[1], "day");

        const isRowPastEnd = Boolean(empEndDateStr && rowDate && rowDate > empEndDateStr);
        const showNoteIcon =
          !isSingleDayRange &&
          Boolean(rowDate) &&
          (record.has_day_note || (!isRowPastEnd && (isAdminOrManager || !record.isPayrollApproved)));
        const hasMenu = Boolean(record.isGroup);

        if (!showNoteIcon && !hasMenu) return "-";

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {showNoteIcon && (
              <span
                role="button"
                className="cursor-pointer d-inline-flex align-items-center justify-content-center"
                title={record.has_day_note ? "View Day Notes" : "Add Day Note"}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "5px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #231F20",
                  transition: "all 0.2s",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDayNoteDate(rowDate);
                  setSelectedDayNotePayrollApproved(Boolean(record.isPayrollApproved));
                  setIsDayNoteModalOpen(true);
                }}
              >
                <span
                  className="svg-20 d-inline-flex align-items-center justify-content-center"
                  style={{
                    filter: "brightness(0)",
                  }}
                >
                  {svg?.employeeNotes}
                </span>
              </span>
            )}

            {hasMenu && (
              <Dropdown
                overlay={groupMenu}
                trigger={["click"]}
                placement="bottomRight"
              >
                <span
                  className="cursor-pointer d-flex align-items-center justify-content-center svg-32"
                  style={{ fontWeight: "bold", fontSize: "20px" }}
                >
                  {svg?.tableMoreOption}
                </span>
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  let employeeNameStr = "";
  if (
    employeeAttendanceData &&
    employeeAttendanceData.length > 0 &&
    employeeAttendanceData[0].user
  ) {
    employeeNameStr = `${decryptByText(
      employeeAttendanceData[0].user.first_name,
    )} ${decryptByText(employeeAttendanceData[0].user.last_name)}`;
  } else if (selectedEmployee && teamMemberList) {
    const emp = teamMemberList.find((e: any) => e._id === selectedEmployee);
    if (emp) {
      employeeNameStr =
        emp.name ||
        `${decryptByText(emp.first_name)} ${decryptByText(emp.last_name)}`;
    }
  }

  let dateTitleStr = "";
  if (
    dateRange &&
    dateRange.length === 2 &&
    dateRange[0] &&
    dateRange[1] &&
    dateRange[0].isSame(dateRange[1], "day")
  ) {
    if (dateRange[0].isSame(dayjs(), "day")) {
      dateTitleStr = "Today";
    } else if (dateRange[0].isSame(dayjs().subtract(1, "day"), "day")) {
      dateTitleStr = "Yesterday";
    }
  }

  const titleText = employeeNameStr
    ? `${dateTitleStr ? `${dateTitleStr} ` : ""}Attendance`
    : "Attendance";

  const showEmployeeSelect = !id && !isPersonalHistory;

  const isSingleDayRange = Boolean(
    dateRange &&
    dateRange.length === 2 &&
    dateRange[0] &&
    dateRange[1] &&
    dateRange[0].isSame(dateRange[1], "day"),
  );

  const singleDayDateStr = isSingleDayRange && dateRange?.[0]
    ? dateRange[0].format("YYYY-MM-DD")
    : "";

  const hasNoteForSingleDay =
    isSingleDayRange && noteDatesSet.has(singleDayDateStr);

  const showViewNote = isSingleDayRange && hasNoteForSingleDay;

  const noteButtonText = showViewNote ? "View Note" : "+ Add Note";

  const singleDayPayrollApproved = React.useMemo(() => {
    if (!groupedData || !Array.isArray(groupedData)) return false;
    if (!isSingleDayRange) {
      const todayStr = formatDateWithBusinessTZ(new Date(), "YYYY-MM-DD");
      const matchingToday = groupedData.find((g: any) => g.date === todayStr);
      return Boolean(matchingToday?.isPayrollApproved);
    }
    const matchingDay = groupedData.find((g: any) => g.date === singleDayDateStr);
    if (matchingDay && matchingDay.isPayrollApproved !== undefined) {
      return Boolean(matchingDay.isPayrollApproved);
    }
    if (Array.isArray(employeeAttendanceData)) {
      return employeeAttendanceData.some(
        (r: any) =>
          r?.created_at &&
          formatDateWithBusinessTZ(r.created_at, "YYYY-MM-DD") === singleDayDateStr &&
          (r.isPayrollApproved === true || r.is_payroll_approved === true),
      );
    }
    return false;
  }, [isSingleDayRange, singleDayDateStr, groupedData, employeeAttendanceData]);

  const isTargetDatePastEnd = Boolean(
    empEndDateStr &&
    (isSingleDayRange && singleDayDateStr
      ? singleDayDateStr > empEndDateStr
      : isEmploymentEnded)
  );

  const showHeaderNoteButton =
    (showViewNote || !isTargetDatePastEnd) &&
    (isAdminOrManager || showViewNote || !singleDayPayrollApproved);

  const handleOpenDayNote = () => {
    let targetDate: string | undefined = undefined;
    let isApproved: boolean | undefined = undefined;

    if (isSingleDayRange && dateRange?.[0]) {
      targetDate = dateRange[0].format("YYYY-MM-DD");
      const matchingDay = Array.isArray(groupedData)
        ? groupedData.find((g: any) => g.date === targetDate)
        : undefined;
      if (matchingDay && matchingDay.isPayrollApproved !== undefined) {
        isApproved = Boolean(matchingDay.isPayrollApproved);
      } else if (Array.isArray(employeeAttendanceData)) {
        isApproved = employeeAttendanceData.some(
          (r: any) =>
            r?.created_at &&
            formatDateWithBusinessTZ(r.created_at, "YYYY-MM-DD") === targetDate &&
            (r.isPayrollApproved === true || r.is_payroll_approved === true),
        );
      }
    } else {
      const todayStr = formatDateWithBusinessTZ(new Date(), "YYYY-MM-DD");
      const matchingToday = Array.isArray(groupedData)
        ? groupedData.find((g: any) => g.date === todayStr)
        : undefined;
      if (matchingToday && matchingToday.isPayrollApproved !== undefined) {
        isApproved = Boolean(matchingToday.isPayrollApproved);
      }
    }

    setSelectedDayNoteDate(targetDate);
    setSelectedDayNotePayrollApproved(isApproved);
    setIsDayNoteModalOpen(true);
  };

  if (!hasLoadedOnce.current && (authLoading || getEmployeeAttendanceDashboardLoading)) {
    return (
      <div className="h-100 d-flex align-items-center justify-content-center">
        <FullScreenLoader />
      </div>
    );
  }

  const isAllowedToView = Boolean(
    userData?.isEmployee ||
    userRole === "employee" ||
    String(userData?.role?.name || "").toLowerCase() === "employee" ||
    USER_PERMISSIONS?.READ ||
    userRole === ROLE.ADMIN
  );

  if (!isAllowedToView) return <NoPermissionPage />;

  const renderMobileBanner = () => {
    if (!isNative && !IS_WEB_ATTENDANCE_ENABLED) return null;
    const isEmp = Boolean(
      userData?.isEmployee ||
      userRole === "employee" ||
      String(userData?.role?.name || "").toLowerCase() === "employee"
    );
    if (
      !isEmp ||
      !userData?.is_employee_setup
    )
      return null;
    if (!userData?.basic_details?.onSite) return null;

    const personalEmpEndDate =
      empEndDateStr ||
      getEmploymentEndDateString(
        userData?.employment_end_date ||
        userData?.employee_details?.basic_details?.employment_end_date ||
        userData?.basic_details?.employment_end_date,
      );
    if (personalEmpEndDate && todayDateStr >= personalEmpEndDate) {
      return null;
    }

    let checkInTimeStr = "-";
    if (employeeAttendanceData && employeeAttendanceData.length > 0) {
      const latestRecord = employeeAttendanceData[0];
      if (
        latestRecord?.created_at &&
        formatDateWithBusinessTZ(latestRecord.created_at, "YYYY-MM-DD") ===
        businessToday.format("YYYY-MM-DD")
      ) {
        checkInTimeStr = formatTimeWithBusinessTZ(
          latestRecord.created_at,
          "hh:mm A",
        );
      }
    }

    if (!userData?.isClockedIn) {
      return (
        <div
          className="px-3 py-2 mb-3 shadow-sm rounded-4 d-flex justify-content-between align-items-center"
          style={{ border: "1px solid #ffccc7", background: "#fff2f0" }}
        >
          <div className="d-flex flex-column pe-2">
            <span className="fw-bold text-dark fs-6 mb-0">
              You have not clocked in!
            </span>
            <span className="text-secondary fw-medium">Clock-in: -</span>
          </div>
          <button
            className="btn text-white fw-bold px-4 py-1.5 shrink-0 text-nowrap"
            style={{
              borderRadius: "10px",
              backgroundColor: "#389e0d",
              borderColor: "#389e0d",
            }}
            onClick={() =>
              canOpenFaceScanModal
                ? setIsPersonalFaceScanModalOpen(true)
                : handleFaceScanUnavailable()
            }
          >
            Clock In
          </button>
        </div>
      );
    } else if (!userData?.isClockedOut) {
      return (
        <div
          className="px-3 py-2 mb-3 shadow-sm rounded-4 d-flex justify-content-between align-items-center"
          style={{ border: "1px solid #b7eb8f", background: "#f6ffed" }}
        >
          <div className="d-flex flex-column pe-2">
            <span className="fw-bold text-dark fs-6 mb-0">
              You have clocked in!
            </span>
            <span className="fw-medium" style={{ color: "#389e0d" }}>
              Clock-in: {checkInTimeStr}
            </span>
          </div>
          <button
            className="btn text-white fw-bold px-4 py-1.5 shrink-0 text-nowrap"
            style={{
              borderRadius: "10px",
              backgroundColor: "#ff1f1f",
              borderColor: "#ff1f1f",
            }}
            onClick={() =>
              canOpenFaceScanModal
                ? setIsPersonalFaceScanModalOpen(true)
                : handleFaceScanUnavailable()
            }
          >
            Clock Out
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {renderMobileBanner()}
      {/* Desktop & Tablet Header Layout */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="text-black fs-36 fw-bolder page-title mb-0 d-flex align-items-center gap-3">
          {id && (
            <span
              className="cursor-pointer svg-24"
              onClick={() => navigate(-1)}
            >
              {svg?.backArrow || "←"}
            </span>
          )}
          {titleText}
        </h2>
        <div className="d-flex align-items-center gap-2 mob-w-100">
          {showEmployeeSelect && (
            <Select
              showSearch
              allowClear
              placeholder="Select Employee"
              className="form-control form-control-lg dateSelectFilter border-s p-0"
              style={{ width: 200 }}
              value={selectedEmployee || undefined}
              onChange={(val) => setSelectedEmployee(val || "")}
              onSearch={(val) => {
                setEmployeeSearch(val);
                setEmployeePage(1);
              }}
              onClear={() => {
                setEmployeeSearch("");
                setEmployeePage(1);
              }}
              onPopupScroll={handleEmployeePopupScroll}
              options={teamMemberList?.map((emp: any) => ({
                label: emp.name,
                value: emp._id,
              }))}
              filterOption={false}
            />
          )}

          <div
            className={`dateSelectFilter RangeDatePicker border-s mw-100 d-inline-block p-0`}
          >
            <RangePicker
              presets={dynamicRangePresets}
              {...(rangePickerProps as any)}
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              suffixIcon={svg?.calenderIcon}
              inputReadOnly={Capacitor.isNativePlatform()}
              placement="bottomRight"
              popupClassName="ipad-range-picker"
            />
          </div>
          {showHeaderNoteButton && (
            <button
              type="button"
              className="btn btn-lg btn-primary mob-btn d-flex align-items-center justify-content-center gap-2 flex-shrink-0"
              onClick={handleOpenDayNote}

            >
              {showViewNote && <EyeOutlined />}
              <span>{noteButtonText}</span>
            </button>
          )}
        </div>
      </div>



      <div className={`card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto`}>
        <div className={`card-body overflow-y-auto`}>
          {!hasLoadedOnce.current && (authLoading || getEmployeeAttendanceDashboardLoading) ? (
            <FullScreenLoader />
          ) : groupedData && groupedData.length > 0 ? (
            !isMobile ? (

              <Table
                className="table-stripped-1 custom-table"
                loading={getEmployeeAttendanceDashboardLoading}
                columns={columns}
                dataSource={groupedData}
                pagination={false}
                bordered
                rowKey="key"
                scroll={{ x: "max-content" }}
              />

            ) : (

              <div className="d-flex flex-column gap-3 px-3">
                {groupedData?.map((record) => {
                  const rowDate =
                    record.date ||
                    (record.created_at
                      ? formatDateWithBusinessTZ(record.created_at, "YYYY-MM-DD")
                      : undefined);

                  if (record.isMissing) {
                    return (
                      <div className="w-100" key={record.key}>
                        <div className="card mobile-table-card mobile-font-s rounded-4 p-3 shadow-sm" style={{ background: "#f8f9fa" }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold fs-6">
                              {record.created_at ? formatDateWithBusinessTZ(record.created_at) : "-"}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              {!isSingleDayRange && rowDate && (isAdminOrManager || !record.isPayrollApproved || record.has_day_note) && (
                                <span
                                  role="button"
                                  className="cursor-pointer d-inline-flex align-items-center justify-content-center"
                                  title={record.has_day_note ? "View Day Notes" : "Add Day Note"}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "5px",
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #231F20",
                                    transition: "all 0.2s",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDayNoteDate(rowDate);
                                    setSelectedDayNotePayrollApproved(Boolean(record.isPayrollApproved));
                                    setIsDayNoteModalOpen(true);
                                  }}
                                >
                                  <span
                                    className="svg-20 d-inline-flex align-items-center justify-content-center"
                                    style={{
                                      filter: "brightness(0)",
                                    }}
                                  >
                                    {svg?.employeeNotes}
                                  </span>
                                </span>
                              )}
                              <span className="text-muted fw-medium fs-6">No Record</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const groupMenu = (
                    <Menu>
                      <Menu.Item
                        key="view"
                        onClick={() => {
                          const selectedDay = dayjs(record.date);
                          setDateRange([
                            selectedDay.startOf("day"),
                            selectedDay.endOf("day"),
                          ]);
                        }}
                      >
                        View Details
                      </Menu.Item>
                    </Menu>
                  );

                  const clockInIcon = record.clockIn?.biometric_authentication_by === "face"
                    ? hr.face
                    : record.clockIn?.biometric_authentication_by === "fingerprint"
                      ? hr.fingerprint
                      : null;

                  const clockOutIcon = record.clockOut?.biometric_authentication_by === "face"
                    ? hr.face
                    : record.clockOut?.biometric_authentication_by === "fingerprint"
                      ? hr.fingerprint
                      : null;

                  return (
                    <div className="w-100" key={record.key}>
                      <div className="card mobile-table-card mobile-font-s rounded-4 p-3 shadow-sm">
                        {/* Card Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                          <span className="fw-bold fs-6" style={{ color: "#333" }}>
                            {record.created_at ? formatDateWithBusinessTZ(record.created_at) : "-"}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            {!isSingleDayRange && rowDate && (record.has_day_note || (!Boolean(empEndDateStr && rowDate > empEndDateStr) && (isAdminOrManager || !record.isPayrollApproved))) && (
                              <span
                                role="button"
                                className="cursor-pointer d-inline-flex align-items-center justify-content-center"
                                title={record.has_day_note ? "View Day Notes" : "Add Day Note"}
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "5px",
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #231F20",
                                  transition: "all 0.2s",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDayNoteDate(rowDate);
                                  setSelectedDayNotePayrollApproved(Boolean(record.isPayrollApproved));
                                  setIsDayNoteModalOpen(true);
                                }}
                              >
                                <span
                                  className="svg-20 d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    filter: "brightness(0)",
                                  }}
                                >
                                  {svg?.employeeNotes}
                                </span>
                              </span>
                            )}
                            {record.isGroup && (
                              <Dropdown overlay={groupMenu} trigger={["click"]} placement="bottomRight">
                                <span className="cursor-pointer d-flex align-items-center justify-content-center svg-32">
                                  {svg?.tableMoreOption}
                                </span>
                              </Dropdown>
                            )}
                          </div>
                        </div>

                        {/* Card Details Grid */}
                        <div className="mobile-card-details fs-6 fs-6-mob">
                          <div className="row gy-2">
                            {/* Clock In */}
                            <div className="col-12 d-flex justify-content-between align-items-center">
                              <span className="text-secondary fw-medium">Clock In</span>
                              <div className="d-flex align-items-center gap-2">
                                <div className="d-flex align-items-center gap-1">
                                  {!isTimeVariant && clockInIcon && (
                                    <AutoCloseTooltip
                                      title={
                                        record.clockIn?.biometric_authentication_by === "face"
                                          ? "Face Recognition"
                                          : "Fingerprint"
                                      }
                                    >
                                      <span
                                        className="flex-shrink-0"
                                        style={{ width: "20px", height: "20px", minWidth: "20px", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: record.clockIn?.biometric_authentication?.face?.photo ? "pointer" : "default" }}
                                        onClick={() => {
                                          if (record.clockIn?.biometric_authentication?.face?.photo) {
                                            setPreviewPhoto(record.clockIn.biometric_authentication.face.photo);
                                            setIsPreviewModalOpen(true);
                                          }
                                        }}
                                      >
                                        <div className="card-ui-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                          {clockInIcon}
                                        </div>
                                      </span>
                                    </AutoCloseTooltip>
                                  )}
                                  <AutoCloseTooltip
                                    title={
                                      record.clockIn?.ppe_compliant
                                        ? "Weared PPE Mask Gloves"
                                        : "Not Weared PPE Mask Gloves"
                                    }
                                  >
                                    <span className="flex-shrink-0" style={{ width: "20px", height: "20px", minWidth: "20px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                      <div className="card-ui-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {record.clockIn?.ppe_compliant ? hr.ppe_compliant.wear : hr.ppe_compliant.not_wear}
                                      </div>
                                    </span>
                                  </AutoCloseTooltip>
                                </div>
                                <span className="fw-medium text-dark">
                                  {record.created_at ? formatTimeWithBusinessTZ(record.created_at) : "---"}
                                </span>
                              </div>
                            </div>

                            {/* Clock Out */}
                            <div className="col-12 d-flex justify-content-between align-items-center">
                              <span className="text-secondary fw-medium">Clock Out</span>
                              <div className="d-flex align-items-center gap-2">
                                <div className="d-flex align-items-center gap-1">
                                  {!isTimeVariant && clockOutIcon && (
                                    <AutoCloseTooltip
                                      title={
                                        record.clockOut?.biometric_authentication_by === "face"
                                          ? "Face Recognition"
                                          : "Fingerprint"
                                      }
                                    >
                                      <span
                                        className="flex-shrink-0"
                                        style={{ width: "20px", height: "20px", minWidth: "20px", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: record.clockOut?.biometric_authentication?.face?.photo ? "pointer" : "default" }}
                                        onClick={() => {
                                          if (record.clockOut?.biometric_authentication?.face?.photo) {
                                            setPreviewPhoto(record.clockOut.biometric_authentication.face.photo);
                                            setIsPreviewModalOpen(true);
                                          }
                                        }}
                                      >
                                        <div className="card-ui-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                          {clockOutIcon}
                                        </div>
                                      </span>
                                    </AutoCloseTooltip>
                                  )}
                                </div>
                                <span className="fw-medium text-dark">
                                  {record.clockOut && record.updated_at ? formatTimeWithBusinessTZ(record.updated_at) : "-"}
                                </span>
                              </div>
                            </div>

                            {/* Total Clocked Hours */}
                            <div className="col-12 d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                              <span className="text-secondary fw-medium">Clocked Hours</span>
                              <span className="fw-medium text-dark">
                                {record.total || "-"}
                              </span>
                            </div>

                            {/* Additional Details for Date Ranges */}
                            {!(dateRange && dateRange.length === 2 && dateRange[0].isSame(dateRange[1], 'day')) && (
                              <>
                                {showTimeOffHours && (
                                  <div className="col-12 d-flex justify-content-between align-items-center">
                                    <span className="text-secondary fw-medium">Time Off Hours</span>
                                    <span className="fw-medium text-dark">
                                      {!record.isGroup || record.isMissing ? "-" : `- ${record.time_off_hours || "00:00"}`}
                                    </span>
                                  </div>
                                )}

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Late Time</span>
                                  <span className="fw-medium text-dark">
                                    {!record.isGroup || record.isMissing ? "-" : `- ${record.late_time || "00:00:00"}`}
                                  </span>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Break Time</span>
                                  <span className="fw-medium text-dark">
                                    {!record.isGroup || record.isMissing ? "-" : `- ${record.break_time || "00:00:00"}`}
                                  </span>
                                </div>

                                {isPayWithinSchedule && (
                                  <div className="col-12 d-flex justify-content-between align-items-center">
                                    <span className="text-secondary fw-medium">Unscheduled Hours</span>
                                    <span className="fw-medium text-dark">
                                      {!record.isGroup || record.isMissing ? "-" : `- ${record.unschedule_time || "00:00:00"}`}
                                    </span>
                                  </div>
                                )}

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="text-secondary fw-medium">Working Time</span>
                                  <span className="fw-medium text-dark">
                                    {!record.isGroup || record.isMissing
                                      ? "-"
                                      : record.working_time || record.total || "00:00:00"}
                                  </span>
                                </div>

                                <div className="col-12 d-flex justify-content-between align-items-center">
                                  <span className="fw-bold text-dark">Round Off Time</span>
                                  <span className="fw-bold" style={{ color: "#1677ff", fontSize: '15px' }}>
                                    {!record.isGroup || record.isMissing ? "-" : record.round_off_time || "00:00"}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            )
          ) : (
            <div className="w-100 h-100">
              <NoDataMessage text="You do not have any attendance records yet." />
            </div>
          )}
        </div>
      </div>

      {dateRange && dateRange.length === 2 && (
        <SummaryTicker className="mt-md-3 mt-2 px-3 py-2 rounded attendance-summary-bar w-100 flex-shrink-0">
          {(() => {
            const items = [];
            items.push(<span key="clocked">Total Clocked Hours : {totalTimeStr}</span>);
            if (showTimeOffHours) {
              items.push(<span key="timeOff">Total Time Off Hours : {totalTimeOffStr}</span>);
            }
            items.push(<span key="late">Total Late Time : {totalLateStr}</span>);
            items.push(<span key="break">Total Break Time : {totalBreakStr}</span>);
            if (isPayWithinSchedule) {
              items.push(<span key="unscheduled">Total Unscheduled Hours : {totalUnscheduledStr}</span>);
            }
            items.push(<span key="round">Total Round Off Time : {totalRoundOffStr}</span>);

            return items.map((item, index) => (
              <React.Fragment key={item.key}>
                {item}
                {index < items.length - 1 && <span className="attendance-summary-separator">||</span>}
              </React.Fragment>
            ));
          })()}
        </SummaryTicker>
      )}



      {isModifyModalVisible && (
        <ModifyAttendanceTimeModal
          visible={isModifyModalVisible}
          onClose={() => {
            setIsModifyModalVisible(false);
            setModifyData(null);
          }}
          data={modifyData}
          selectedEmployee={selectedEmployee}
          dateRange={dateRange}
        />
      )}

      {isDeleteModalVisible && (
        <CommonDeleteModal
          visible={isDeleteModalVisible}
          setIsModalVisible={setIsDeleteModalVisible}
          handleDelete={async () => {
            const payload: any = {};
            if (deleteData?._id) payload.clockInId = deleteData._id;

            const success = await dispatch(
              DeleteEmployeeAttendanceAPI(payload) as any,
            );

            if (success) {
              message.success("Attendance deleted successfully");
              fetchAttendance(true, true);
              setIsDeleteModalVisible(false);
              setDeleteData(null);
            }
          }}
          title="Delete Attendance"
          description="Are you sure you want to delete this attendance record? This action cannot be undone."
          isLoading={deleteEmployeeAttendanceLoading}
        />
      )}

      {isPreviewModalOpen && (
        <CommonFilePreviewModal
          visible={isPreviewModalOpen}
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          file={{
            url: previewPhoto ? getFullAwsUrl(previewPhoto) : "",
            name: "Clock Event Photo",
            type: "image/jpeg",
          }}
        />
      )}

      {openBookTimeOffModal && (
        <BookTimeOffModal
          visible={openBookTimeOffModal}
          onCancel={() => setOpenBookTimeOffModal(false)}
          employeeId={userData?._id}
        />
      )}

      {isDayNoteModalOpen && (
        <DayNotesDetailModal
          visible={isDayNoteModalOpen}
          onClose={() => {
            setIsDayNoteModalOpen(false);
            setSelectedDayNoteDate(undefined);
            setSelectedDayNotePayrollApproved(undefined);
          }}
          onSuccess={() => {
            fetchAttendance(true, true);
          }}
          date={selectedDayNoteDate}
          isPayrollApproved={selectedDayNotePayrollApproved}
          userId={isPersonalHistory ? undefined : (id || selectedEmployee || undefined)}
        />
      )}

      {isPersonalFaceScanModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "#0b0e14",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 10000,
            }}
          >
            <button
              className="btn btn-light"
              onClick={() => setIsPersonalFaceScanModalOpen(false)}
              style={{
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloseCircleOutlined />
            </button>
          </div>
          <FaceClockInOut
            isPersonalFaceScan={true}
            onSuccessClose={() => {
              setIsPersonalFaceScanModalOpen(false);
              fetchAttendance(true, true);
              dispatch(GetProfileAPI(navigate, undefined, undefined, true) as any);
            }}
          />
        </div>
      )}
    </>
  );
};

export default ClockInHistory;
